import { z } from "zod";
import {
  VOTE_WEIGHT_SOURCES as DOMAIN_VOTE_WEIGHT_SOURCES,
  DEFAULT_VOTE_WEIGHT_SOURCE as DOMAIN_DEFAULT_VOTE_WEIGHT_SOURCE,
  isVoteWeightSource,
  type VoteWeightSource,
} from "@goppy/domain";

/**
 * Contract config WEIGHTED VOTE + QUORUM-MIN (US-E17.F2-0189-S03 · @UC9254 @UC9256).
 *
 * 2 khoá registry (`system_settings`, override được ở Group.settings / Fund.settings):
 *  - `vote_weight_source` (enum) — equal | by_role | by_contribution. **Default `equal`** ⇒ 1 người
 *    1 phiếu ⇒ quỹ cũ KHÔNG regress.
 *  - `vote_quorum_min` (int ≥ 0) — số NGƯỜI tham gia tối thiểu để phiên hợp lệ. **Default 0** =
 *    không chặn. TÁCH HOÀN TOÀN khỏi `expense_decision_threshold` (tỉ lệ thông qua).
 *
 * Veto = cờ cấp thành viên (`group_members.hasVeto`), KHÔNG phải config quỹ — quản trị qua RBAC
 * member (ngoài phạm vi contract config này).
 *
 * Enum + type-guard DERIVE từ `@goppy/domain` consensus-tally (một nguồn sự thật, không nhân bản).
 */

export const VOTE_WEIGHT_SOURCE_KEY = "vote_weight_source";
export const VOTE_QUORUM_MIN_KEY = "vote_quorum_min";

export const VOTE_WEIGHT_CONFIG_KEY_LIST = [
  VOTE_WEIGHT_SOURCE_KEY,
  VOTE_QUORUM_MIN_KEY,
] as const;

export const VOTE_WEIGHT_SOURCES = DOMAIN_VOTE_WEIGHT_SOURCES;
export const DEFAULT_VOTE_WEIGHT_SOURCE = DOMAIN_DEFAULT_VOTE_WEIGHT_SOURCE;
export type { VoteWeightSource };

/** Nguồn trọng số — CHỈ 3 giá trị; giá trị lạ ⇒ reject (fail-loud ở biên). */
export const voteWeightSourceSchema = z
  .string()
  .refine((v) => isVoteWeightSource(v), {
    message: "Nguồn trọng số phiếu phải là equal | by_role | by_contribution.",
  });

/** Quorum-min = SỐ NGƯỜI (int ≥ 0). Từ chối âm/thập phân (không phải tỉ lệ, không float). */
export const voteQuorumMinSchema = z
  .number()
  .int({ message: "Quorum tối thiểu phải là số nguyên (số NGƯỜI tham gia)." })
  .min(0, { message: "Quorum tối thiểu không được âm." });

/** Body patch config biểu quyết (fund/group). `.strict()` + ≥1 key. */
export const voteWeightConfigPatchSchema = z
  .object({
    voteWeightSource: voteWeightSourceSchema.optional(),
    voteQuorumMin: voteQuorumMinSchema.optional(),
  })
  .strict()
  .refine((body) => Object.keys(body).length > 0, {
    message: "Cần ít nhất 1 thiết lập để cập nhật",
  });

export type VoteWeightConfigPatch = z.infer<typeof voteWeightConfigPatchSchema>;

/** Effective config biểu quyết của 1 quỹ (fund > group > system). */
export const voteWeightConfigResponseSchema = z
  .object({
    voteWeightSource: voteWeightSourceSchema,
    voteQuorumMin: voteQuorumMinSchema,
  })
  .strict();

export type VoteWeightConfigResponse = z.infer<typeof voteWeightConfigResponseSchema>;
